import Glide from '@glidejs/glide'

const classes = {
    bullet: 'glide__bullet',
    glideSlide: 'glide__slide',
    gallerySlides: 'gallery__slides',
    galleryVideo: 'gallery__video',
    modelOptionSlides: 'model-options__slides',
    mowerWrapper: 'model-options__mower-wrapper',
}

/**
 * Carousel using Glide.js library
 *
 * @param {HTMLElement} container
 */
export default function Carousel(container) {
    // Selectors
    const glidePrimary = container.querySelector('.glide--primary')
    const glideSecondary = container.querySelector('.glide--secondary') // Contains fake bullets that behave like carousel on mobile
    const tabBar = container.querySelector('.glide__tab-bar') // Animated border moving under bullets
    const bullets = glidePrimary.querySelectorAll(`.${classes.bullet}`)
    const { publicAccessSelector } = container.dataset

    // Checks
    const hasTabBar = !!tabBar
    const hasSecondaryCarousel = !!glideSecondary
    const isGalleryCarousel = container.classList.contains('gallery__container')
    const isModelOptionsCarousel = container.classList.contains(
        'model-options__container'
    )

    // Carousels shared options
    const animationDuration = 500 // ms
    const type = 'carousel'
    const dragThreshold = false

    // Carousels custom options
    const customCarouselOptions = isModelOptionsCarousel
        ? {
              animationDuration: 3000,
              breakpoints: {
                  1023: { animationDuration },
              },
          }
        : {}

    const customCarouselSecondaryOptions = isGalleryCarousel
        ? {
              gap: 16,
              peek: 0,
              breakpoints: {
                  359: { perView: 2 },
                  559: { peek: 0 },
              },
          }
        : {}

    // Primary carousel instance
    const GlidePrimary = new Glide(glidePrimary, {
        animationDuration,
        type,
        dragThreshold,
        swipeThreshold: 5,
        perTouch: 1,
        ...customCarouselOptions,
    })

    const setPublicControllers = () => {
        if (!publicAccessSelector) return
        const controllers = document.querySelectorAll(publicAccessSelector)

        const publicSelect = ({ currentTarget }) => {
            const { glideDestination } = currentTarget.dataset
            if (!glideDestination) return
            GlidePrimary.go(`=${glideDestination}`)
        }

        controllers.forEach(controller => {
            controller.addEventListener('click', publicSelect)
        })
    }

    setPublicControllers()

    // Secondary carousel instance
    const GlideSecondary =
        hasSecondaryCarousel &&
        new Glide(glideSecondary, {
            animationDuration,
            type,
            focusAt: 'center',
            perView: 3,
            peek: 20, // How much px is visible from the sides of carousel - to see part of other slides
            gap: 0,
            dragThreshold,
            swipeThreshold: false,
            breakpoints: {
                359: {
                    perView: 1,
                    peek: 0,
                },
                559: {
                    peek: -100,
                },
                759: {
                    peek: 0,
                },
            },
            ...customCarouselSecondaryOptions,
        })

    // Define methods

    /**
     * Custom component extending Glide to set autoheight and instant active class names toggling
     *
     * @param {Glide} Glide - Glide component instance
     * @param {Object} Components - Instance components
     * @param {EventsBus} Events - Instance events
     */
    const CustomComponent = (Glide, Components, Events) => {
        const { Build, Controls, Html } = Components
        const modelOptionSlides = container.querySelector(
            `.${classes.modelOptionSlides}`
        )
        const gallerySlides = container.querySelector(
            `.${classes.gallerySlides}`
        )

        Events.on(['mount.after', 'run', 'resize'], () => {
            // Set height of active slide to the carousel
            Html.track.style.height = `${
                Html.slides[Glide.index].offsetHeight
            }px`

            if (hasTabBar) {
                updateTabBar(Glide.index)
            }
        })

        Events.on('move', () => {
            // Toggle active class instantly on carousel move
            if (Build) {
                Build.activeClass()
            }

            if (hasSecondaryCarousel) {
                const activeElementGlideDir = container.querySelector(
                    `.${classes.glideSlide}--active`
                ).dataset.glideDir
                const activeElementClones = container.querySelectorAll(
                    `[data-glide-dir="${activeElementGlideDir}"].${
                        classes.glideSlide
                    }--clone`
                )

                // Add active class to active slides clones
                // Normally, the cloned element will gain the active class after animation finishes. It's needed to add it after movement starts.
                activeElementClones.forEach(element =>
                    element.classList.add(`${classes.glideSlide}--active`)
                )

                // Classes need to be reassigned after every move operation since cloned elements are removed and added from DOM
                removeFakeBulletsEventListeners()
                addFakeBulletsEventListeners()
            }
        })

        Events.on('run', () => {
            // Toggle active class instantly on carousel move
            if (Controls._n) {
                Controls.setActive()
            }
        })

        Events.on('run.before', () => {
            if (isModelOptionsCarousel) {
                // Prevent CSS animation to start after page load
                modelOptionSlides.classList.add(
                    `${classes.modelOptionSlides}--ready`
                )

                // Reinsert element into DOM in order to animation start on each slide move
                // Just to remove and add class does not work - https://css-tricks.com/restart-css-animation/
                modelOptionSlides.classList.add(
                    `${classes.modelOptionSlides}--animating`
                )

                const mowerWrapper = container.querySelector(
                    `.${classes.mowerWrapper}`
                )
                const mowerClone = mowerWrapper.cloneNode(true)
                mowerWrapper.parentNode.replaceChild(mowerClone, mowerWrapper)
                mowerClone.classList.add(`${classes.mowerWrapper}--animating`)
            }

            if (isGalleryCarousel) {
                // Prevent CSS animation to start after page load
                gallerySlides.classList.add(`${classes.gallerySlides}--ready`)

                gallerySlides.classList.add(
                    `${classes.gallerySlides}--animating`
                )
            }
        })

        Events.on('run.after', () => {
            if (isModelOptionsCarousel) {
                // Remove CSS animation after sliding
                modelOptionSlides.classList.remove(
                    `${classes.modelOptionSlides}--animating`
                )
            }

            if (isGalleryCarousel) {
                // Remove CSS animation after sliding
                gallerySlides.classList.remove(
                    `${classes.gallerySlides}--animating`
                )

                // Check if slide contains iframe
                const slideVideo = Html.slides[Glide.index].querySelector(
                    `.${classes.galleryVideo}`
                )

                const slideVideoNotLoaded = !!slideVideo && !slideVideo.src

                if (slideVideoNotLoaded) {
                    // Reset iframe src to stop playing
                    const videoSrc = slideVideo.dataset.videoSrc

                    slideVideo.src = videoSrc
                }
            }
        })

        Events.on(['run.before', 'mount.after'], () => {
            if (isGalleryCarousel) {
                // Check if slide contains iframe
                const slideVideo = Html.slides[Glide.index].querySelector(
                    `.${classes.galleryVideo}`
                )
                const slideHasVideo = !!slideVideo

                if (slideHasVideo) {
                    // Reset iframe src to stop playing
                    const videoSrc = slideVideo.dataset.videoSrc

                    slideVideo.src = videoSrc
                }
            }
        })

        return {}
    }

    /**
     * Mount configured carousels
     */
    function mountCarousels() {
        // Mount secondary carousel first
        if (hasSecondaryCarousel) GlideSecondary.mount()

        // Mount primary carousel
        GlidePrimary.on(['run', 'resize'], function(eventOptions) {
            const sliderIndex = GlidePrimary.index

            if (hasSecondaryCarousel) {
                updateSecondaryCarousel(sliderIndex, eventOptions)
            }
        }).mount({ CustomComponent })
    }

    /**
     * Update width and position of tab bar
     *
     * @param {*} activeIndex - Active carousel slide index
     */
    function updateTabBar(activeIndex) {
        const activeBullet = bullets[activeIndex]

        tabBar.style.width = `${activeBullet.clientWidth}px`
        tabBar.style.transform = `translateX(${activeBullet.offsetLeft}px)`
    }

    /**
     * Update slide index and toggle class of secondary carousel
     *
     * @param {*} activeIndex - Active carousel slide index
     */
    function updateSecondaryCarousel(activeIndex, eventOptions) {
        // Move carousel to the position of primary carousel
        GlideSecondary.go(eventOptions.direction || `=${activeIndex}`)

        // Toggle active class instantly on carousel move
        GlideSecondary._c.Build.activeClass()
    }

    /**
     * Add event listeners to fake bullets to behave like real bullets
     */
    function addFakeBulletsEventListeners() {
        glideSecondary
            .querySelectorAll(
                `.${classes.bullet}:not(.${classes.glideSlide}--active)`
            )
            .forEach(element =>
                element.addEventListener('click', movePrimaryCarousel)
            )
    }

    /**
     * Remove event listeners from fake bullets
     */
    function removeFakeBulletsEventListeners() {
        glideSecondary
            .querySelectorAll(`.${classes.bullet}`)
            .forEach(element => {
                element.removeEventListener('click', movePrimaryCarousel)
            })
    }

    /**
     * Perform primary carousel slide move
     *
     * @param {Event} e
     */
    function movePrimaryCarousel(e) {
        const { currentTarget } = e

        // Decide in which direction will carousel move
        const movePattern = currentTarget.previousElementSibling.classList.contains(
            `${classes.glideSlide}--active`
        )
            ? '>'
            : '<'

        GlidePrimary.go(movePattern)
    }

    // Initialize methods

    mountCarousels()

    if (hasSecondaryCarousel) {
        addFakeBulletsEventListeners()
    }
}
